import type { PostgrestError, SupabaseClient } from '@supabase/supabase-js';
import type { StorageError } from '@supabase/storage-js';
import { getSupabaseClient } from './supabase';
import type { EmailRec, EventContact, PhoneRec } from '../types';

type ClientOption = { supabase?: SupabaseClient };

export type EventContactRow = EventContact & {
  event_id?: string;
  deleted_at?: string | null;
};

const baseSelect =
  'id,event_id,role,first_name,last_name,title,email,phone,phone_type,position,note,excluded,authorized_for_customer_pickup,deleted_at,card_image_path';

const hasIdentifier = (contact: Partial<EventContact>): boolean => {
  const email = String(contact.email || '').trim();
  const phone = String(contact.phone || '').trim();
  const first = String(contact.first_name || '').trim();
  const last = String(contact.last_name || '').trim();
  return Boolean(email) || Boolean(phone) || (Boolean(first) && Boolean(last));
};

export async function fetchEventContacts(
  eventId: string,
  options?: { includeDeleted?: boolean; supabase?: SupabaseClient },
): Promise<{ data: EventContactRow[]; error: PostgrestError | null }> {
  if (!eventId) return { data: [], error: null };
  const supabase = options?.supabase ?? getSupabaseClient();
  let query = supabase.from('event_contacts').select(baseSelect).eq('event_id', eventId).order('position');
  if (!options?.includeDeleted) {
    query = (query as any).is('deleted_at', null);
  }
  const { data, error } = await query;
  if (error) return { data: [], error };
  const ids = Array.isArray(data) ? data.map((row) => row.id) : [];
  let phoneMap: Record<string, PhoneRec[]> = {};
  let emailMap: Record<string, EmailRec[]> = {};
  if (ids.length) {
    const { data: phoneRows } = await supabase
      .from('contact_phones')
      .select('id,entity,contact_id,kind,number,is_primary,label,deleted_at')
      .in('contact_id', ids)
      .eq('entity', 'event')
      .is('deleted_at', null);
    const { data: emailRows } = await supabase
      .from('contact_emails')
      .select('id,entity,contact_id,kind,email,is_primary,label,deleted_at')
      .in('contact_id', ids)
      .eq('entity', 'event')
      .is('deleted_at', null);
    for (const row of phoneRows || []) {
      (phoneMap[row.contact_id] ||= []).push(row as PhoneRec);
    }
    for (const row of emailRows || []) {
      (emailMap[row.contact_id] ||= []).push(row as EmailRec);
    }
  }
  const withLinked = (data as any[]).map(
    (row) => ({ ...(row as any), phones: phoneMap[row.id] || [], emails: emailMap[row.id] || [] }),
  );
  return { data: withLinked as EventContactRow[], error: null };
}

export async function createEventContact(params: {
  eventId: string;
  role?: string;
  initial?: Partial<EventContact>;
  position?: number;
  supabase?: SupabaseClient;
}): Promise<{ data: EventContactRow | null; error: PostgrestError | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const initial = params.initial || {};
  const role = params.role || 'client';
  const lacksEmailPhone = !initial.email && !initial.phone;
  let first = initial.first_name;
  let last = initial.last_name;
  if (lacksEmailPhone) {
    if (!first && !last) {
      first = '(New)';
      last = '(Contact)';
    } else if (first && !last) {
      last = '(Contact)';
    } else if (!first && last) {
      first = '(New)';
    }
  }
  const insert = {
    event_id: params.eventId,
    role,
    position: params.position ?? 0,
    ...initial,
    first_name: first,
    last_name: last,
  };
  const { data, error } = await supabase.from('event_contacts').insert(insert).select(baseSelect).single();
  return {
    data: data ? ({ ...(data as any), phones: [], emails: [] } as EventContactRow) : null,
    error: error ?? null,
  };
}

export async function updateEventContact(params: {
  contactId: string;
  patch: Partial<EventContact>;
  supabase?: SupabaseClient;
}): Promise<PostgrestError | null> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { error } = await supabase.from('event_contacts').update(params.patch).eq('id', params.contactId);
  return error ?? null;
}

export async function softDeleteEventContact(params: {
  contactId: string;
  supabase?: SupabaseClient;
}): Promise<PostgrestError | null> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { error } = await supabase.rpc('soft_delete', { table_name: 'event_contacts', _id: params.contactId });
  return error ?? null;
}

/**
 * Saves each contact's position with its own update. There is no batch function in the
 * database (the update_event_contact_order RPC this once tried first never existed).
 */
export async function reorderEventContacts(params: {
  contacts: Array<{ id: string; position: number }>;
  supabase?: SupabaseClient;
}): Promise<PostgrestError | null> {
  const supabase = params.supabase ?? getSupabaseClient();
  const results = await Promise.all(
    params.contacts.map((c) =>
      supabase.from('event_contacts').update({ position: c.position }).eq('id', c.id),
    ),
  );
  return results.find((res) => res.error)?.error ?? null;
}

export async function upsertEventContactPhones(params: {
  contactId: string;
  phones: Array<{ kind: 'cell' | 'office' | 'other'; number: string; is_primary?: boolean; label?: string | null }>;
  supabase?: SupabaseClient;
}): Promise<PostgrestError | null> {
  if (!params.phones || !params.phones.length) return null;
  const supabase = params.supabase ?? getSupabaseClient();
  const seen = new Set<string>();
  const rows = params.phones
    .map((p) => ({
      entity: 'event',
      contact_id: params.contactId,
      kind: p.kind,
      number: String(p.number || '').trim(),
      is_primary: !!p.is_primary,
      label: p.label || null,
    }))
    .filter((p) => p.number)
    .filter((p) => {
      const key = p.number.replace(/\D/g, '');
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  if (!rows.length) return null;
  const { error } = await supabase.from('contact_phones').insert(rows);
  return error ?? null;
}

export async function upsertEventContactEmails(params: {
  contactId: string;
  emails: Array<{ kind: 'work' | 'personal' | 'other'; email: string; is_primary?: boolean; label?: string | null }>;
  supabase?: SupabaseClient;
}): Promise<PostgrestError | null> {
  if (!params.emails || !params.emails.length) return null;
  const supabase = params.supabase ?? getSupabaseClient();
  const seen = new Set<string>();
  const rows = params.emails
    .map((e) => ({
      entity: 'event',
      contact_id: params.contactId,
      kind: e.kind,
      email: String(e.email || '').trim().toLowerCase(),
      is_primary: !!e.is_primary,
      label: e.label || null,
    }))
    .filter((e) => e.email)
    .filter((e) => {
      if (seen.has(e.email)) return false;
      seen.add(e.email);
      return true;
    });
  if (!rows.length) return null;
  const { error } = await supabase.from('contact_emails').insert(rows);
  return error ?? null;
}

export async function replaceEventContactPhones(params: {
  contactId: string;
  phones: Array<{ kind: 'cell' | 'office' | 'other'; number: string; is_primary?: boolean; label?: string | null }>;
  supabase?: SupabaseClient;
}): Promise<PostgrestError | null> {
  const supabase = params.supabase ?? getSupabaseClient();
  // Stop if the old phones stay live: inserting the new ones would leave both.
  const { error: clearError } = await supabase
    .from('contact_phones')
    .update({ deleted_at: new Date().toISOString() })
    .eq('entity', 'event')
    .eq('contact_id', params.contactId)
    .is('deleted_at', null);
  if (clearError) return clearError;
  if (params.phones && params.phones.length) {
    let primaryIndex = params.phones.findIndex((p) => p.is_primary);
    if (primaryIndex < 0) {
      primaryIndex = Math.max(0, params.phones.findIndex((p) => p.kind === 'cell'));
      if (primaryIndex < 0) primaryIndex = 0;
    }
    const rows = params.phones.map((p, idx) => ({
      entity: 'event',
      contact_id: params.contactId,
      kind: p.kind,
      number: p.number,
      is_primary: idx === primaryIndex,
      label: p.label || null,
    }));
    const { error } = await supabase.from('contact_phones').insert(rows);
    if (error) return error;
  }
  return null;
}

export async function replaceEventContactEmails(params: {
  contactId: string;
  emails: Array<{ kind: 'work' | 'personal' | 'other'; email: string; is_primary?: boolean; label?: string | null }>;
  supabase?: SupabaseClient;
}): Promise<PostgrestError | null> {
  const supabase = params.supabase ?? getSupabaseClient();
  // Stop if the old emails stay live: inserting the new ones would leave both.
  const { error: clearError } = await supabase
    .from('contact_emails')
    .update({ deleted_at: new Date().toISOString() })
    .eq('entity', 'event')
    .eq('contact_id', params.contactId)
    .is('deleted_at', null);
  if (clearError) return clearError;
  if (params.emails && params.emails.length) {
    let primaryIndex = params.emails.findIndex((e) => e.is_primary);
    if (primaryIndex < 0) primaryIndex = 0;
    const rows = params.emails.map((e, idx) => ({
      entity: 'event',
      contact_id: params.contactId,
      kind: e.kind,
      email: e.email,
      is_primary: idx === primaryIndex,
      label: e.label || null,
    }));
    const { error } = await supabase.from('contact_emails').insert(rows);
    if (error) return error;
  }
  return null;
}

export type EventClientMeta = {
  id: string | null;
  business: string | null;
  primary_contact_id: string | null;
};

export async function fetchEventClientMeta(
  eventId: string,
  options?: ClientOption,
): Promise<{ data: EventClientMeta | null; error: PostgrestError | null }> {
  if (!eventId) return { data: null, error: null };
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('events')
    .select('client_id,client_business,primary_contact_id,clients:client_id(business_name)')
    .eq('id', eventId)
    .maybeSingle();
  if (error) return { data: null, error };
  const row = data as any;
  return {
    data: data
      ? {
          id: row.client_id ?? null,
          business: row.client_business ?? row.clients?.business_name ?? null,
          primary_contact_id: row.primary_contact_id ?? null,
        }
      : null,
    error: null,
  };
}

export async function setEventPrimaryContact(params: {
  eventId: string;
  contactId: string | null;
  supabase?: SupabaseClient;
}): Promise<PostgrestError | null> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { error } = await supabase
    .from('events')
    .update({ primary_contact_id: params.contactId })
    .eq('id', params.eventId);
  return error ?? null;
}

export async function attachClientToEvent(params: {
  eventId: string;
  clientId: string;
  business: string;
  importContacts: boolean;
  supabase?: SupabaseClient;
}): Promise<{ error: PostgrestError | null; usedFallback: boolean }> {
  const supabase = params.supabase ?? getSupabaseClient();
  if (params.importContacts) {
    const { error: rpcError } = await supabase.rpc('attach_client_to_event', {
      _event_id: params.eventId,
      _client_id: params.clientId,
    });
    if (!rpcError) return { error: null, usedFallback: false };
    const { error: fallbackError } = await supabase
      .from('events')
      .update({ client_id: params.clientId, client_business: params.business })
      .eq('id', params.eventId);
    return { error: fallbackError ?? null, usedFallback: true };
  }
  const { error } = await supabase
    .from('events')
    .update({ client_id: params.clientId, client_business: params.business })
    .eq('id', params.eventId);
  return { error: error ?? null, usedFallback: false };
}

export async function fetchEventVenueId(
  eventId: string,
  options?: ClientOption,
): Promise<{ data: string | null; error: PostgrestError | null }> {
  if (!eventId) return { data: null, error: null };
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('event_venues')
    .select('venue_id')
    .eq('event_id', eventId)
    .is('deleted_at', null)
    .limit(1)
    .maybeSingle();
  if (error) return { data: null, error };
  return { data: (data as any)?.venue_id ?? null, error: null };
}

export async function uploadContactCardImage(params: {
  eventId: string;
  contactId: string;
  file: File;
  supabase?: SupabaseClient;
}): Promise<{ path: string | null; error: StorageError | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const ext = (params.file.name.split('.').pop() || 'jpg').toLowerCase();
  const path = `event/${params.eventId}/${params.contactId}-${Date.now()}.${ext}`;
  const contentType = params.file.type || (ext === 'png' ? 'image/png' : 'image/jpeg');
  const { error } = await supabase.storage
    .from('contact-cards')
    .upload(path, params.file, { upsert: false, contentType });
  if (error) return { path: null, error };
  return { path, error: null };
}

export async function getContactCardSignedUrl(
  storagePath: string,
  options?: ClientOption,
): Promise<{ signedUrl: string | null; error: StorageError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  try {
    const { data, error } = await supabase.storage.from('contact-cards').createSignedUrl(storagePath, 60);
    if (error) return { signedUrl: null, error };
    return { signedUrl: data?.signedUrl ?? null, error: null };
  } catch (err: any) {
    return { signedUrl: null, error: err as StorageError };
  }
}

export { hasIdentifier as eventContactHasIdentifier };
